import { WAD } from '@/lib/launchpad/constants'

export function mulDiv(a: bigint, b: bigint, denominator: bigint): bigint {
  if (denominator === 0n)
    throw new Error('Division by zero')
  return (a * b) / denominator
}

export function bpsOf(amount: bigint, bps: number): bigint {
  if (bps <= 0)
    return 0n
  return mulDiv(amount, BigInt(bps), 10_000n)
}

/** Parse a decimal ETH string (e.g. "1.25") into wei. Display/input helper only. */
export function parseEthToWei(input: string): bigint {
  const trimmed = input.trim()
  if (!trimmed || trimmed.startsWith('-'))
    return 0n
  const [wholeRaw, fracRaw = ''] = trimmed.split('.')
  const whole = wholeRaw === '' ? 0n : BigInt(wholeRaw)
  const fracPadded = `${fracRaw}000000000000000000`.slice(0, 18)
  const frac = BigInt(fracPadded)
  return whole * WAD + frac
}

export function weiToEthString(wei: bigint, maxDecimals = 6): string {
  if (typeof wei !== 'bigint')
    return '0'
  const negative = wei < 0n
  const abs = negative ? -wei : wei
  const whole = abs / WAD
  const frac = abs % WAD
  if (frac === 0n)
    return `${negative ? '-' : ''}${whole.toString()}`
  const fracStr = frac.toString().padStart(18, '0').replace(/0+$/, '')
  const clipped = fracStr.slice(0, maxDecimals).replace(/0+$/, '')
  if (!clipped)
    return `${negative ? '-' : ''}${whole.toString()}`
  return `${negative ? '-' : ''}${whole.toString()}.${clipped}`
}

/** Compact ETH label for price/mcap/raised. Always decimal — never scientific notation. */
export function formatCompactEth(wei: bigint): string {
  if (typeof wei !== 'bigint' || wei === 0n)
    return '0'
  const negative = wei < 0n
  const abs = negative ? -wei : wei
  const whole = abs / WAD
  if (whole >= 1n)
    return weiToEthString(wei, 4)
  const fracStr = (abs % WAD).toString().padStart(18, '0')
  const idx = fracStr.search(/[1-9]/)
  if (idx === -1)
    return '0'
  const end = Math.min(18, idx + 4)
  const clipped = fracStr.slice(0, end).replace(/0+$/, '')
  return `${negative ? '-' : ''}0.${clipped}`
}

/** Display-only. Do not use for persisted balances. */
export function weiToEthNumber(wei: bigint | undefined | null): number {
  // Full 18-decimal conversion. Clipping first (e.g. 12 dp) flattens sub-1e-10
  // token spots so consecutive buys/sells look like the same chart price.
  if (typeof wei !== 'bigint')
    return 0
  return Number(wei) / Number(WAD)
}

export function bigintToJson(value: bigint): string {
  return value.toString()
}

export function jsonToBigint(value: unknown): bigint {
  if (typeof value === 'bigint')
    return value
  if (typeof value === 'number' && Number.isFinite(value))
    return BigInt(Math.trunc(value))
  if (typeof value === 'string' && /^-?\d+$/.test(value))
    return BigInt(value)
  return 0n
}
